<template>
  <div>
    <my-header></my-header>
  
    <div class="cotests-box">
      <div class="h-panel">
        <b-button @click="showHelp">Help</b-button>

        <b-button
          v-if="mayStartContest"
          :to="{name: 'new_contest'}"
          variant="primary"
        >
          New contest
        </b-button>

        <b-button
          :disabled="isDeleteContestsButtonDisabled"
          :variant="deleteContestsButtonVariant"
          @click="deleteContestsWithConfirmation"
        >
          Delete
        </b-button>
      </div>

      <h2>Contests</h2>
      <table class="mb-2">
        <tr>
          <th class="pr-1">
            <label for="type-selector" class="mb-0">Type</label>
          </th>
          <td class="pr-4">
            <b-form-select
              id="type-selector"
              v-model="type"
              :options="typeOptions"
            />
          </td>
          <th class="pr-1">
            <label for="status-selector" class="mb-0">Status</label>
          </th>
          <td class="pr-4">
            <b-form-select
              id="status-selector"
              v-model="status"
              :options="statusOptions"
            />
          </td>

          <th class="pr-1">
            <label for="window-past-duration-selector" class="mb-0">Past</label>
          </th>
          <td class="pr-4">
            <b-form-select
              id="window-past-duration-selector"
              v-model="windowPastDuration"
              :options="windowDurationOptions"
            />
          </td>

          <th class="pr-1">
            <label for="window-future-duration-selector" class="mb-0">Future</label>
          </th>
          <td>
            <b-form-select
              id="window-future-duration-selector"
              v-model="windowFutureDuration"
              :options="windowDurationOptions"
            />
          </td>
        </tr>
      </table>

      <template v-if="canShowFilteredContests">
        <contests-plot
          :contests="filteredContests"
          :windowPeriod="windowPeriod"
          v-on:contest-clicked="showContestInfo"
        />

        <contest-table
          :contestContainers="filteredContestContainers"
          :nowSec="nowSec"
          :mayManageContests="mayManageContests"
          v-on:contests-selection-requested="selectContests"
          v-on:contests-deselection-requested="deselectContests"
          v-on:contest-deletion-requested="deleteContestWithConfirmation"
          v-on:contest-finalization-requested="finalizeContestWithConfirmation"
        />
      </template>

      <loading-indicator
        v-if="isLoading"
      />

      <p v-if="message !== null" class="mt-3">
        {{message}}
      </p>

    </div>

    <contests-help-window ref="help-window"/>

  </div>
</template>

<script>
/**
 * @typedef { import('../auth.js').Actor } Actor
 */
import auth from '../auth.js';

import { HTTP } from '../http-common.js';
import notifier from '../notifier.js';
import MyHeader from './Header.vue';
import LoadingIndicator from './LoadingIndicator.vue';
import ContestTable from './ContestTable.vue';
import ContestsPlot from './ContestsPlot.vue';
import ContestsHelpWindow from './ContestsHelpWindow.vue';

/**
 * @typedef { import('../model/contest.js').ContestPreview          } ContestPreview
 * @typedef { import('../model/contest.js').ContestPreviewContainer } ContestPreviewContainer
 * @typedef { import('../model/contest.js').ContestStatus           } ContestStatus
 */

import { CONTEST_STATUS_FINISHED          } from '../model/contest.js';
import { getContestTypeOptionsForFilter   } from '../model/contest.js';
import { getContestStatusOptionsForFilter } from '../model/contest.js';

/**
 * @typedef { import('../service/confirm.js').ConfirmHandle } ConfirmHandle
 */
import { confirmAction } from '../service/confirm.js';

import { TIME_UNIT_MONTH          } from '../model/event_time_filter.js';
import { getWindowDurationOptions } from '../model/event_time_filter.js';
import { getBegin                 } from '../model/event_time_filter.js';
import { getEnd                   } from '../model/event_time_filter.js';

import { mayStartTmnt as utilMayStartContest     } from '../util.js';
import { mayManageTmnts as utilMayManageContests } from '../util.js';
import { convertMomentToStamp                    } from '../util.js';

const LOAD_STATE_INIT          = 1;
const LOAD_STATE_LOADING       = 2;
const LOAD_STATE_LOADED_NORMAL = 3;
const LOAD_STATE_LOADED_ERROR  = 4;

export default {
  name: 'Contests',

  components: {
    MyHeader,
    LoadingIndicator,
    ContestsPlot,
    ContestTable,
    ContestsHelpWindow,
  },

  data() {
    const DURATION_1_MONTH = {timeUnit: TIME_UNIT_MONTH, amount: 1};

    return {
      /** @type {?Actor} */
      actor: auth.getViewer(),

      loadState: LOAD_STATE_INIT,

      /** @type {ContestPreviewContainer[] | null} */
      contestContainers: null,

      //-----------------------------------------------------------------------
      // filter

      typeOptions: getContestTypeOptionsForFilter(),
      type: null,

      statusOptions: getContestStatusOptionsForFilter(),
      status: null,

      windowDurationOptions: getWindowDurationOptions(),
      windowPastDuration:   DURATION_1_MONTH,
      windowFutureDuration: DURATION_1_MONTH,

      windowPeriod: null,

      //-----------------------------------------------------------------------

      /** @type {?number} */
      timerId: null,

      nowSec: Date.now() / 1000,
    };
  },

  computed: {
    /** @returns {boolean} */
    isLoading() {
      return this.loadState === LOAD_STATE_LOADING;
    },

    /** @returns {boolean} */
    isLoadedNormal() {
      return this.loadState === LOAD_STATE_LOADED_NORMAL;
    },

    /** @returns {?string} */
    message() {
      const conts = this.contestContainers;
      if(conts !== null && conts.length === 0) {
        return 'No contests.';
      }

      const fCContainers = this.filteredContestContainers;
      if(fCContainers !== null && fCContainers.length === 0) {
        return 'No contests match the specified filter.';
      }

      return null;
    },

    /** @returns {ContestPreviewContainer[] | null} */
    filteredContestContainers() {
      const containers = this.contestContainers;
      if(containers === null) {
        return null;
      }

      if(this.type === null && this.status === null) {
        return containers;
      }

      return containers.filter(
        container => isContestMatched(container.contest, this.type, this.status)
      );
    },

    /** @returns {ContestPreview[] | null} */
    filteredContests() {
      const containers = this.filteredContestContainers;
      if(containers === null) {
        return null;
      }

      const contests = [];

      for(const container of containers) {
        contests.push(container.contest);
      }

      return contests;
    },

    /** @returns {boolean} */
    hasFilteredContests() {
      const filteredContestContainers = this.filteredContestContainers;
      if(filteredContestContainers === null) {
        return false;
      }

      return filteredContestContainers.length > 0;
    },

    /** @returns {boolean} */
    canShowFilteredContests() {
      return this.isLoadedNormal && this.hasFilteredContests;
    },

    /** @returns {boolean} */
    isDeleteContestsButtonDisabled() {
      return !this.hasSelectedFilteredContests;
    },

    /** @returns {boolean} */
    hasSelectedFilteredContests() {
      const containers = this.filteredContestContainers;
      if(containers === null) {
        return false;
      }

      for(const container of containers) {
        if(container.selected) {
          return true;
        }
      }

      return false;
    },

    /** @returns {number[] | null} */
    selectedFilteredContestIds() {
      const containers = this.filteredContestContainers;
      if(containers === null) {
        return null;
      }

      const ids = [];
      for(const container of containers) {
        if(container.selected) {
          ids.push(container.contest.id);
        }
      }

      return ids;
    },

    /** @returns {string} */
    deleteContestsButtonVariant() {
      if(this.hasSelectedFilteredContests) {
        return 'danger';
      } else {
        return 'light';
      }
    },

    /** @returns {boolean} */
    mayStartContest() {
      const actor = this.actor;
      if(actor === null) {
        return false;
      }

      return utilMayStartContest(actor.role);
    },

    /** @returns {boolean} */
    mayManageContests() {
      const actor = this.actor;
      if(actor === null) {
        return false;
      }

      return utilMayManageContests(actor.role);
    },
  },

  created() {
    this.timerId = setInterval(this.updateTime, 1000);
    this.loadContests();
  },

  beforeDestroy() {
    if(this.timerId !== null) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
  },

  watch: {
    windowPastDuration() {
      this.loadContests();
    },

    windowFutureDuration() {
      this.loadContests();
    },
  },

  methods: {
    loadContests() {
      notifier.deleteSenderNotifications(this);

      this.loadState = LOAD_STATE_LOADING;

      const url = '/contests';

      const requestConf = {
        params: {}
      };

      const now = convertMomentToStamp(new Date());
      const period = getPeriod(now, this.windowPastDuration, this.windowFutureDuration);
      this.windowPeriod = period;

      if(period.begin !== 0) {
        requestConf.params.begin = period.begin;
      }
      if(period.end !== 0) {
        requestConf.params.end = period.end;
      }

      HTTP.get(url, requestConf)
      .then(response => {
        /** @type {ContestPreview[] | null} */
        const data = response.data.data;
        
        if(data === null) {
          notifier.showError('Invalid server response. data === null.', {sender: this});
          return;
        }

        const contests = data;
        contests.sort((a, b) => b.stime - a.stime);

        this.contestContainers = convertContestPreviewsToContestPreviewContainers(contests);

        this.loadState = LOAD_STATE_LOADED_NORMAL;
      })
      .catch(err => {
        this.loadState = LOAD_STATE_LOADED_ERROR;
        notifier.showError(err, {sender: this});
      });
    },

    updateTime() {
      this.nowSec = Date.now() / 1000;
    },

    /**
     * @param {number} contestId
     */
    showContestInfo(contestId) {
      this.$router.push({name: 'contest_info', params: {contestId: contestId}});
    },

    //-------------------------------------------------------------------------
    // Selection
    //-------------------------------------------------------------------------

    selectContests() {
      for(const container of this.contestContainers) {
        if(!container.selected) {
          container.selected = true;
        }
      }
    },

    deselectContests() {
      for(const container of this.contestContainers) {
        if(container.selected) {
          container.selected = false;
        }
      }
    },

    //-------------------------------------------------------------------------
    // Finish
    //-------------------------------------------------------------------------

    /**
     * @param {ContestPreview} contest
     */
    finalizeContestWithConfirmation(contest) {
      this.confirmContestFinalization(() => {
        this.finalizeContest(contest);
      });
    },

    /**
     * @param {ConfirmHandle} onConfirmed
     */
    confirmContestFinalization(onConfirmed) {
      confirmAction(
        this,
        'Are you sure you want to finish this contest {env}?',
        'Finish',
        onConfirmed,
      );
    },

    /**
     * @param {ContestPreview} contest
     */
    finalizeContest(contest) {
      notifier.deleteSenderNotifications(this);
      
      const url = `/contest/${contest.id}/finalize`;
      HTTP.post(url)
      .then(response => {
        const data = response.data.data;
        if(data === null) {
          notifier.showError('Invalid server response. data === null.', {sender: this});
          return;
        }

        contest.frtime = data.frtime;
        contest.status = CONTEST_STATUS_FINISHED;
        notifier.showSuccess('Contest finished.');
      })
      .catch(err => {
        notifier.showError(err, {sender: this});
      });
    },

    //-------------------------------------------------------------------------
    // Deletion
    //-------------------------------------------------------------------------

    /**
     * @param {ContestPreview} contest
     */
     deleteContestWithConfirmation(contest) {
      notifier.deleteSenderNotifications(this);

      this.confirmContestDeletion(
        contest.id,
        () => {
          this.deleteContest(contest);
        }
      );
    },

    /**
     * @param {ConfirmHandle} onConfirmed
     */
    confirmContestDeletion(contestId, onConfirmed) {
      confirmAction(
        this,
        getContestDeletionConfirmationMessage(contestId),
        'Delete',
        onConfirmed,
      );
    },

    /**
     * @param {ContestPreview} contest
     */
    deleteContest(contest) {
      const url = `/contest/${contest.id}/del`;
      HTTP.post(url)
      .then(response => {
        const data = response.data.data;
        if(data === null) {
          notifier.showError('Invalid server response. data === null.', {sender: this});
          return;
        }

        deleteContestContainerFromArray(this.contestContainers, contest.id);
        notifier.showSuccess('Contest deleted.');
      })
      .catch(err => {
        notifier.showError(err, {sender: this});
      });
    },

    //-------------------------------------------------------------------------

    deleteContestsWithConfirmation() {
      notifier.deleteSenderNotifications(this);

      const contestIds = this.selectedFilteredContestIds;

      this.confirmContestsDeletion(
        contestIds,
        () => {
          this.deleteContests(contestIds);
        },
      );
    },

    /**
     * @param {number[]} contestIds
     * @param {ConfirmHandle} onConfirmed
     */
    confirmContestsDeletion(contestIds, onConfirmed) {
      const params = {};
      if(contestIds.length > 1) {
        params.description = getContestIdsInfo(contestIds);
      }

      confirmAction(
        this,
        getContestsDeletionConfirmationMessage(contestIds),
        'Delete',
        onConfirmed,
        params,
      );
    },

    /** @param {number[]} contestIds */
    deleteContests(contestIds) {
      const idsStr = contestIds.join(',');

      const data = new FormData();
      data.set('ids', idsStr);

      HTTP.post('/contests/delete', data)
      .then(() => {
        deleteContestContainersFromArray(this.contestContainers, contestIds);

        const message = getDeletionCompleteMessage(contestIds.length);
        notifier.showSuccess(message);
      })
      .catch(err => {
        notifier.showError(err, {sender: this});
      });
    },

    //-------------------------------------------------------------------------

    showHelp() {
      const helpWindow = this.$refs['help-window'];
      helpWindow.show();
    },
  },
};

function getPeriod(stamp, windowPastDuration, windowFutureDuration) {
  const begin = (windowPastDuration   !== null) ? getBegin(stamp, windowPastDuration  ) : 0;
  const end   = (windowFutureDuration !== null) ? getEnd(  stamp, windowFutureDuration) : 0;

  return {
    begin: begin,
    end: end,
  };
}

/**
 * @param {ContestPreview} contest
 * @param {?number} type
 * @param {?ContestStatus} status
 * @returns {boolean}
 */
function isContestMatched(contest, type, status) {
  const mustCheckType = (type !== null);
  if(mustCheckType && contest.type !== type) {
    return false;
  }

  const mustCheckStatus = (status !== null);
  if(mustCheckStatus && contest.status !== status) {
    return false;
  }

  return true;
}

/**
 * @param {ContestPreview[]} contests
 * @returns {ContestPreviewContainer[]}
 */
function convertContestPreviewsToContestPreviewContainers(contests) {
  const containers = [];

  for(const contest of contests) {
    const container = {
      contest: contest,
      selected: false,
    };
    containers.push(container);
  }

  return containers;
}

/**
 * @param {ContestPreviewContainer[]} contestContainers 
 * @param {number[]} contestIds
 */
function deleteContestContainersFromArray(contestContainers, contestIds) {
  for(const contestId of contestIds) {
    deleteContestContainerFromArray(contestContainers, contestId);
  }
}

/**
 * @param {ContestPreviewContainer[]} contestContainers 
 * @param {number} contestId
 */
function deleteContestContainerFromArray(contestContainers, contestId) {
  const index = getContestContainerIndex(contestContainers, contestId);
  if(index !== null) {
    contestContainers.splice(index, 1);
  }
}

/**
 * @param {ContestPreviewContainer[]} contestContainers
 * @param {number} contestId
 * @returns {number | null}
 */
function getContestContainerIndex(contestContainers, contestId) {
  for(let i = 0; i < contestContainers.length; i++) {
    const container = contestContainers[i];
    if(container.contest.id == contestId) {
      return i;
    }
  }

  return null;
}

function getContestDeletionConfirmationMessage(id) {
  return `Are you sure you want to delete contest ${id} {env}?`
}

function getContestsDeletionConfirmationMessage(ids) {
  if(ids.length === 1) {
    const id = ids[0];
    return getContestDeletionConfirmationMessage(id);
  }

  const amount = ids.length;
  return `Are you sure you want to delete ${amount} contests {env}?`;
}

function getContestIdsInfo(ids) {
  return 'Contest ids: ' + ids.join(', ') + '.';
}

function getDeletionCompleteMessage(amount) {
  if(amount === 1) {
    return 'Contest deleted.'
  } else {
    return 'Contests deleted.'
  }
}
</script>

<style scoped>
.cotests-box {
  padding-top: 1em;
  margin-left: 1em;
  margin-bottom: 1rem;
}
</style>
